import React, { Component } from "react";
import { connect } from "react-redux";
import URLSearchParams from 'url-search-params'
import { Redirect, Route, Switch } from "react-router-dom";
import { LocaleProvider, message } from "antd";
import { IntlProvider } from "react-intl";
import moment from 'moment-timezone';
import './style.css';

import AppLocale from "lngProvider";
import MainApp from "./MainApp";
import SignIn from "../SignIn";
import ResetPassword from "../ResetPassword";
import PublicTracking from "../PublicTracking";
import { setInitUrl } from "appRedux/actions/Auth";
import { onLayoutTypeChange, onNavStyleChange, setThemeType } from "appRedux/actions/Setting";
import { hasSession } from 'util/session';

import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  THEME_TYPE_DARK,
  THEME_TYPE_LITE,
  THEME_TYPE_SEMI_DARK
} from "../../constants/ThemeSetting";
import { getUser } from "../../appRedux/actions/Auth";
import { fetchModules } from "../../appRedux/actions";
import ErrorPage from "../Error";

// moment.tz.setDefault('America/New_York');
const RestrictedRoute = ({ component: Component, token, ...rest }) =>
  <Route
    {...rest}
    render={props =>
      token
        ? <Component {...props} />
        : <Redirect
          to={{
            pathname: '/login',
            state: { from: props.location }
          }}
        />}
  />;


class App extends Component {

  updatePageTitle = (authUser) => {
    const defaultTitle = 'AssetPro : Asset Management System';

    if (authUser && authUser.organisation && authUser.organisation.name) {
      const orgName = authUser.organisation.name;
      document.title = `AssetPro: ${orgName}`;
    } else {
      document.title = defaultTitle;
    }
  };

  setLayoutType = (layoutType) => {
    if (layoutType === LAYOUT_TYPE_FULL) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('full-layout');
    } else if (layoutType === LAYOUT_TYPE_BOXED) {
      document.body.classList.remove('full-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('boxed-layout');
    } else if (layoutType === LAYOUT_TYPE_FRAMED) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('full-layout');
      document.body.classList.add('framed-layout');
    }
  };

  setNavStyle = (navStyle) => {
    if (navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
      navStyle === NAV_STYLE_DARK_HORIZONTAL ||
      navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
      navStyle === NAV_STYLE_ABOVE_HEADER ||
      navStyle === NAV_STYLE_BELOW_HEADER) {
      document.body.classList.add('full-scroll');
      document.body.classList.add('horizontal-layout');
    } else {
      document.body.classList.remove('full-scroll');
      document.body.classList.remove('horizontal-layout');
    }
  };

  componentDidMount() {
    const params = new URLSearchParams(this.props.location.search);
    if (params.has("theme")) {
      this.props.setThemeType(params.get('theme'));
    } else {
      const storedTheme = localStorage.getItem('themeType');
      if (storedTheme && [THEME_TYPE_DARK, THEME_TYPE_LITE, THEME_TYPE_SEMI_DARK].includes(storedTheme)) {
        this.props.setThemeType(storedTheme);
      }
    }
    if (params.has("nav-style")) {
      this.props.onNavStyleChange(params.get('nav-style'));
    }
    if (params.has("layout-type")) {
      this.props.onLayoutTypeChange(params.get('layout-type'));

    }
    if (hasSession()) {
      this.props.getUser();
      this.props.fetchModules();
    }

    if (this.props && this.props.authUser) {
      // console.log(this.props.authUser.timezone, moment.tz.guess(true));
      if (this.props.authUser && this.props.authUser.timezone)
        moment.tz.setDefault(this.props.authUser.timezone);
      else
        moment.tz.setDefault(moment.tz.guess(true));
    }

    // Update page title with organisation name
    this.updatePageTitle(this.props.authUser);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.themeType !== this.props.themeType) {
      if (this.props.themeType === THEME_TYPE_DARK) {
        document.body.classList.add('dark-theme');
      } else {
        document.body.classList.remove('dark-theme');
      }
    }

    // Update page title when auth user changes
    if (prevProps.authUser !== this.props.authUser) {
      this.updatePageTitle(this.props.authUser);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps && nextProps.authUser && JSON.stringify(nextProps.authUser) !== JSON.stringify(this.props.authUser)) {
      // console.log(nextProps.authUser.timezone, moment.tz.guess(true));
      if (nextProps.authUser && nextProps.authUser.timezone)
        moment.tz.setDefault(nextProps.authUser.timezone);
      else
        moment.tz.setDefault(moment.tz.guess(true));

      // Update page title when auth user changes
      this.updatePageTitle(nextProps.authUser);
    }

    // if(nextProps && nextProps.error && JSON.stringify(nextProps.error)!==JSON.stringify(this.props.error)){
    //   message.error(nextProps.error)
    // }
  }

  render() {
    // have to set timezone
    const { match, location, themeType, layoutType, navStyle, locale, token } = this.props;
    if (themeType === THEME_TYPE_DARK) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
    if (location.pathname === '/') {
      if (!token) {
        return (<Redirect to={'/login'} />);
      } else {
        return (<Redirect to={'/dashboard'} />);
      }
    }
    this.setLayoutType(layoutType);

    this.setNavStyle(navStyle);

    const currentAppLocale = AppLocale[locale.locale];
    return (
      <LocaleProvider locale={currentAppLocale.antd}>
        <IntlProvider
          locale={currentAppLocale.locale}
          messages={currentAppLocale.messages}>

          <Switch>
            <Route path='/resetPassword' component={ResetPassword} />
            <Route exact path='/login' component={SignIn} />
            <Route exact path='/error' component={ErrorPage} />
            <Route path='/public/track/:asset/:id/:token' component={PublicTracking} />
            <RestrictedRoute path={`${match.url}`} token={token}
              component={MainApp} />
          </Switch>
        </IntlProvider>
      </LocaleProvider>
    )
  }
}

const mapStateToProps = ({ settings, auth, commonData }) => {
  const { locale, navStyle, themeType, layoutType } = settings;
  const { authUser, token, initURL } = auth;
  return { locale, token, navStyle, themeType, layoutType, authUser, initURL, error: commonData.error }
};
export default connect(mapStateToProps, {
  setInitUrl,
  getUser,
  setThemeType,
  onNavStyleChange,
  onLayoutTypeChange,
  fetchModules
})(App);
